import React from 'react';
import cn from 'classnames';
import Assets from 'assets/icons';
import { IColumn } from 'interfaces';
import FilterDropdown from './FilterDropdown';
import { useTranslation } from 'react-i18next';
import classes from './UsersTable.module.scss';
import { useNavigate } from 'react-router';
import TableLoader from 'components/Sceletons/TableLoader';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsConfirmBlockOpen, setIsConfirmDeleteOpen, setProcessedUsers, setUserChoosen } from 'store/slices/dataUsersSlice';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { createGLobalChat } from 'store/reducers/chatCreator';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import { changeBlockStatus, defaultGroups, deleteInvite, deleteUserHandle, reInviteHandle } from 'store/reducers/usersGroupCreator';
import { setIsRSidebarCollapsed } from 'store/slices/windowStateSlice';
import { IDataUserTable, IPendingInviteUsers, IUsers } from 'interfaces';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { statusIcons } from 'components/SideBar/UsersTab/Settings/PersonInformation';
import { filtersForColumns, getUsersColumns, onCustomFilter } from './columns/userColumns';
import { Avatar, Button, ConfigProvider, Dropdown, Empty, MenuProps, Popover, Select, Table, Tag, Tooltip } from 'antd';
import ConfirmDeleteUser from 'components/Modals/ConfirmDeleteUser/ConfirmDeleteUser';
import ConfirmChangeStatus from 'components/Modals/ConfirmChangeStatus/ConfirmChangeStatus';

type IParentInviteRow = IPendingInviteUsers & {
  type: 'invite_link';
  link_name: string;
  link: string;
  max_invites: number | null;
  used_count: number;
  child_invites: IPendingInviteUsers[];
};

type UsersTableType = {
  changeRole: (user: IUsers, group_id: string) => Promise<void>
  isLoadingRooms: boolean;
};

const columnsWithFilters = ['role', 'status'];

const UsersTable: React.FC<UsersTableType> = ({ changeRole, isLoadingRooms }) => {
  const [modifiedColumns, setModifiedColumns] = React.useState<IColumn[]>();
  const [installingNewRole, setInstallingNewRole] = React.useState<string[]>([]);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = React.useState<boolean>(false);
  const [allUsers, setAllUsers] = React.useState<(IUsers | IPendingInviteUsers | IParentInviteRow)[]>([]);

  const { isGroupsAndUsersLoading, users, pendingInviteUsers, groups, selectedUser,
    rolesPermissions, userWithIdProcessed, isConfirmDeleteOpen, isConfirmBlockOpen } = useAppSelector((state) => state.dataUsers);  
  const { isLoadingUserPermissions, userData, userPermissions } = useAppSelector((store) => store.userData);
  const { userFoldersPermissions } = useAppSelector((store) => store.permissions);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { messageApi } = useAppSelector((store) => store.windowState);

  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isAvailableEditUserGroup = userPermissions?.can_edit_roles;
  const isAvailableExcludeUser = userPermissions?.can_exclude_users;
  const isAvailableInviteUser = userPermissions?.can_invite_users;

  const groupsSelectOptions = groups.map((group) => ({
    label: defaultGroups.includes(group.name) ? t(`Users.table.${group.name}`) : group.name,
    value: group.id,
    key: group.key,
    permissions: group?.role?.permissions,
  }));

  React.useEffect(() => {
    const regularInvites = pendingInviteUsers
    .filter(i => !i.is_parent_invite && !i.parent_invite_id)
    .map(invite => ({ ...invite, key: invite.id, type: 'invite' }));
    const childInvites = pendingInviteUsers.filter(i => i.parent_invite_id);
    const parentRows: IParentInviteRow[] = pendingInviteUsers
      .filter(i => i.is_parent_invite)
      .map(parent => ({
        ...parent,
        key: parent.id,
        type: 'invite_link' as const,
        link_name: parent.invite_name ?? '',
        link: parent.invite_link ?? '',
        max_invites: parent.max_invites ?? null,
        used_count: childInvites.filter(c => c.parent_invite_id === parent.id && c.invite_status === 1).length,
        child_invites: childInvites.filter(c => c.parent_invite_id === parent.id),
      }));

    setAllUsers([
      ...users,
      ...regularInvites,
      ...parentRows,
    ].map((user: any) => ({
      ...user,
      name: user.type === 'user'
        ? `${user?.first_name!} ${user?.last_name}`
        : user.type === 'invite_link'
          ? user.link_name
          : user.email,
    })));
  }, [users, pendingInviteUsers]);

  const onFilterDropdownOpenChange = (open: boolean) => setIsFilterDropdownOpen(open);
  
  React.useEffect(() => {
    const usersColumns = getUsersColumns(t);
    const customColumns = usersColumns.map((col: IColumn) => ({
      ...col,
      filterDropdownProps: {
        onOpenChange: onFilterDropdownOpenChange
      },
      filterIcon: col.dataIndex === 'name' && ((filtered: boolean) => (
        <Assets.SearchIcon className={filtered ? classes.primaryFilter : classes.greyIcon} />
      )),
      filters: columnsWithFilters.includes(col.dataIndex) && filtersForColumns(t, allUsers, col.dataIndex, groupsSelectOptions, dataRoom?.owner_id),
      onFilter: columnsWithFilters.includes(col.dataIndex) && ((value: string, record: any) => columnsWithFilters.includes(col.dataIndex) && onCustomFilter(value, record, col.dataIndex, dataRoom?.owner_id)),
      filterDropdown: col.dataIndex === 'name' && (
        ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: FilterDropdownProps) => (
          <FilterDropdown
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters!}
            isFilterDropdownOpen={isFilterDropdownOpen}
            close={close}
            dataIndex={col.dataIndex}
            setAllUsers={setAllUsers}
          />
        )
      ),
      render: (value: any, info: any) => renderCell({ value, info, col }),
    }));
    setModifiedColumns(customColumns);
  }, [i18n.language, groups, installingNewRole, pendingInviteUsers, selectedUser, userPermissions, allUsers]);

  const renderPermissionsList = React.useCallback((rolePermissions?: string[]) => {
    if (!rolesPermissions?.length) return null;
    return (
      <div className={classes.permissionList}>
        {rolesPermissions.map((permission) => (
          <div key={permission.id} className={classes.permissionRow}>
            <span className={classes.permissionIcon}>
              {statusIcons[String(rolePermissions?.includes(permission.type)) as keyof typeof statusIcons]}
            </span>
            <span className={classes.permissionName}>{permission.name}</span>
          </div>
        ))}
      </div>
    );
  }, [rolesPermissions]);

  const onChatClick = async (info: IUsers) => {
    if (info) {
      const { payload } = (await dispatch(
        createGLobalChat({
          data: {
            members_ids: [userData?.id!, info.id],
            entity_type: 3,
            room_id: dataRoom?.id!,
            title: info.email,
          },
          ownerId: userData?.id!,
          oponentName: `${info.first_name} ${info.last_name}`,
        })
      )) as any;      
      navigate(`/global-chats/${payload?.chat?.id || info.id}/`);
    }
  };

  const onDeactivateLink = async (linkId: string) => {
    try {
      dispatch(setProcessedUsers([...userWithIdProcessed, linkId]));
      await dispatch(deleteInvite({ invite_id: linkId, t }));
    } catch {
      messageApi?.error(t('Users.table.inviteLink.deactivateError'));
    } finally {
      dispatch(setProcessedUsers(userWithIdProcessed.filter(id => id !== linkId)));
    }
  };

  const renderCell = ({ info, col, value }: any) => {
    if (info.type === 'invite_link') {
      if (col.key === 'name') {
        const isLimitReached = info.max_invites !== null && info.used_count >= info.max_invites;

        const onCopyLink = (e: React.MouseEvent) => {
          e.stopPropagation();
          navigator.clipboard.writeText(info.link);
          messageApi?.success(t('Users.table.inviteLink.linkCopied'));
        };

        return (
          <div className={classes.rowWrap}>
            <Tooltip title={isLimitReached ? '' : t('Users.table.inviteLink.copyLink')}>
              <Button
                type='text'
                size='middle'
                className={classes.copyLinkBtn}
                icon={<CopyOutlined className={classes.linkIcon}  />}
                disabled={isLimitReached}
                onClick={onCopyLink}
              />
            </Tooltip>
            <span className={classes.linkName}>{info.link_name}</span>
          </div>
        );
      }
      if (col.key === 'role') {
        const targetGroupName = groupsSelectOptions.find((el: any) => el.value === info.group_id)?.label;
        return <span>{targetGroupName || '-'}</span>;
      }
      if (col.key === 'status') {
        return (
          <span className={classes.accepted}>
            {t('Users.table.statuses.accept')} {info.used_count}/{info.max_invites ?? '∞'}
          </span>
        );
      }
      if (col.key === 'entered_at') {
        return (
          <div className={classes.withDetails}>
            <div className={classes.textWrap}>-</div>
            {isAvailableExcludeUser && (
              <div className={classes.btnWrap}>
                <Dropdown
                  menu={{
                    items: [{
                      key: 'deactivate_link',
                      className: cn(classes.controlMenuItem, classes.warning),
                      label: (
                        <span className={classes.spanItem}>
                          <Assets.TrashIcon /> {t('Users.table.inviteLink.deactivate')}
                        </span>
                      ),
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        onDeactivateLink(info.id);
                      },
                    }],
                  }}
                  placement='bottomRight'
                  classNames={{ root: classes.controlMenu }}
                >
                  <Button
                    className={classes.detailIconBtn}
                    size='small'
                    type='text'
                    onClick={(e) => e.stopPropagation()}
                    icon={<Assets.VerticalIcon />}
                  />
                </Dropdown>
              </div>
            )}
          </div>
        )
      }
      return null;
    }

    const isRoomOwnerCard = info?.id === dataRoom?.owner_id;
    const itIsMe = info?.id === userData?.id;
    const targetGroup = groups?.find(group => group?.group_id === (info.group_id || info.group[0]));
    const isFullAdmin = targetGroup?.group_name === 'full_administrator'
    const availabelToChangeRole = !isRoomOwnerCard && (!itIsMe || isFullAdmin);
    
    let itemsDetails: MenuProps['items'] = [];
    if (info.type === 'user') {
      itemsDetails.push({
        key: 'chat',
        className: classes.controlMenuItem,
        label: (
          <span className={classes.spanItem}>
            <Assets.ChatIcon /> {t('Users.HelperMenu.chat')}
          </span>
          ),
        onClick: (e) => {
          e.domEvent.stopPropagation();
          onChatClick(info);
        },
      });
    };

    if (!isRoomOwnerCard && info.type === 'user' && isAvailableExcludeUser) {
      itemsDetails.push({
        type: 'divider',
        className: classes.divider,
      });
      itemsDetails.push({
        key: 'delete',
        className: cn(classes.controlMenuItem, classes.warning),
        label: (
          <span className={classes.spanItem}>
            <Assets.TrashIcon /> {t('Users.HelperMenu.delete')}
          </span>
        ),
        onClick: (e) => {
          e.domEvent.stopPropagation();
          dispatch(setUserChoosen(info || null));
          dispatch(setIsConfirmDeleteOpen(true));
        }
      });

      itemsDetails.push({
        type: 'divider',
        className: classes.divider,
      });
      itemsDetails.push({
        key: 'block',
        className: cn(classes.controlMenuItem, classes.warning),
        label: (
          <span className={classes.spanItem}>
            <Assets.LockIcon /> { info.is_blocked ? t('Users.HelperMenu.unblock') : t('Users.HelperMenu.block') }
          </span>
        ),
        onClick: (e) => {
          e.domEvent.stopPropagation();
          dispatch(setUserChoosen(info || null));
          dispatch(setIsConfirmBlockOpen(true));
        }
      });
    }

    if (info.type === 'invite' && info.invite_status !== 3 && isAvailableExcludeUser) {
      itemsDetails.push({
        key: 'delete_invite',
        className: classes.controlMenuItem,
        label: (
          <span className={classes.spanItem}>
            <Assets.TrashIcon /> {t('Users.Tabs.actions.cancel')}
          </span>
          ),
        onClick: async (e) => {
          e.domEvent.stopPropagation();
          try{
            dispatch(setProcessedUsers([...userWithIdProcessed, info.id]));
            await dispatch(deleteInvite({invite_id: info.id, t}));
          } catch { } finally {
            dispatch(setProcessedUsers(userWithIdProcessed.filter(id => id !== info.id)));
          }
        },
      });

      if ((info.is_expired || info.invite_status === 2) && isAvailableInviteUser) {

        itemsDetails.push({
          type: 'divider',
          className: classes.divider,
        });
        itemsDetails.push({
          key: 'reinvite',
          className: classes.controlMenuItem,
          label: (
            <span className={classes.spanItem}>
              <ReloadOutlined style={{transform: 'rotateY(180deg)', padding: '0 3px'}}/> {t('Users.Tabs.actions.reinvite')}
            </span>
          ),
          onClick: async (e) => {
            e.domEvent.stopPropagation();
            try{
              dispatch(setProcessedUsers([...userWithIdProcessed, info.id]));
              await dispatch(reInviteHandle({currentUser: info, t}));
            } catch { } finally {
              dispatch(setProcessedUsers(userWithIdProcessed.filter(id => id !== info.id)));
            }
          }
        });
      }
    }

    if (col.key === 'name') {
      if (info.first_name || info.last_name) {
        return (
          <div className={classes.rowWrap}>
            <Avatar className={classes.avatar}>
              <span className={classes.avatarWord}>{info.first_name[0]}</span>
            </Avatar>
            {isRoomOwnerCard && (
              <Tooltip title={t('Users.table.owner')}>
                <Tag variant='filled' className={classes.tagStatus}>{t('Rooms.badges.own')}</Tag>
              </Tooltip>
            )}
            <span>{`${info.first_name} ${info.last_name}`}</span>
            {info.is_blocked && <Tooltip className={classes.block} title={t('Users.table.userBlocked')}>
              <span><Assets.LockIcon /></span>
            </Tooltip>}
          </div>
        );
      } else {
        return <div className={classes.rowWrap}>
          <Avatar className={classes.avatar}>
            <span className={classes.avatarWord}>{info.email[0]}</span>
          </Avatar>
          <span className={classes.inviteColor}>{info.email}</span>
        </div>
      };
    };

    if (col.key === 'role') {
      const currentValue = (info?.group && info?.group[0]) || info?.group_id;
      const targetGroupName = groupsSelectOptions.find((el) => el.value === currentValue)?.label;
      const rolePermissionsForUser = targetGroup?.role?.permissions;

      return currentValue ? (
        <div>
          {isAvailableEditUserGroup && availabelToChangeRole ? (
            <ConfigProvider theme={{ components: { Select: { optionPadding: '5px 2px 5px 8px' } } }}>
              <Select
                placeholder={t('PersonalInfo.none')}
                size='small'
                variant='borderless'
                loading={installingNewRole.includes(info.id)}
                disabled={isRoomOwnerCard || installingNewRole.includes(info.id)}
                options={groupsSelectOptions}
                value={currentValue}
                optionRender={(option) => {
                  const optionPermissions = option.data?.permissions
                    || groups.find((group) => group.id === option.value || group.group_id === option.value)?.role?.permissions;

                  return (
                    <div className={classes.infoTooltip}>
                      <span>{defaultGroups.includes(option.label as string) ? t(`Users.table.${option.label}`) : option.label}</span>
                      <ConfigProvider theme={{ components: { Popover: { zIndexPopup: 1051 } } }}>
                        <Popover placement='right' content={renderPermissionsList(optionPermissions)} title=''>
                          <span><Assets.InfoIcon /></span>
                        </Popover>
                      </ConfigProvider>
                    </div>
                  )
                }}
                popupMatchSelectWidth={false}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleChangeGroup(e, info)}
              />
            </ConfigProvider>
          ) : (
            <div className={classes.userDescription}>
              <span>{targetGroupName}</span>
              {rolesPermissions?.length ? (
                <Popover placement='right' content={renderPermissionsList(rolePermissionsForUser)} title=''>
                  <span><Assets.InfoIcon className={classes.permissionInfoIcon} /></span>
                </Popover>
              ) : null}
            </div>
          )}
        </div>
      ) : '-'
    }

    if(col.key === 'status'){
      // if (isRoomOwnerCard) return t('Users.table.statuses.owner')
      if (info.type === 'user') {
        return <span className={classes.accepted}>{t('Users.table.statuses.accept')}</span>;
      } else if (info.is_expired) {
        return <span className={classes.awaiting}>{t('Users.table.statuses.expired')}</span>;
      } else if (info.invite_status === 0) {
        return <span className={classes.awaiting}>{t('Users.table.statuses.awaiting')}</span>;
      } else if (info.invite_status === 2) {
        return <span className={classes.rejected}>{t('Users.table.statuses.rejected')}</span>;
      }
    };

    if (col.key === 'entered_at') {
      const isSelectedUser = info.id === selectedUser?.id;
      
      return <div className={cn(classes.withDetails, isSelectedUser ? classes.selected : 'null')}>
        <div className={classes.textWrap}>
          {value
            ? getDateWithTimeZone(value, userData?.date_format!)
            : '-'
          }
        </div>
        {info.id !== userData?.id && itemsDetails[0] && (
          <div className={classes.btnWrap}>
            <Dropdown
              menu={{ items: itemsDetails }}
              placement='bottomRight'
              classNames={{ root: classes.controlMenu }}
            >
              <Button
                className={classes.detailIconBtn}
                size='small'
                type='text'
                onClick={(e) => e.stopPropagation()}
                icon={<Assets.VerticalIcon />}
              />
            </Dropdown>
          </div>
        )}
      </div>;
    };
    return value;
  };

  const onRowClick = (record: (IDataUserTable | IUsers | IPendingInviteUsers), rowIndex: any) => {
    if (record.type === 'user') {
      dispatch(setUserChoosen(record as IUsers));
      dispatch(setIsRSidebarCollapsed({
        collapsed: false,
        entity: 'user_data',
        width: 300,
      }));
    };
  };

  const handleChangeGroup = async (value: any, user: IUsers) => {
    setInstallingNewRole(prev => [...prev, user.id]);
    try {
      await changeRole(user, value);
    } catch (err) { } finally {
      setInstallingNewRole(prev => prev.filter(id => id !== user.id));
    };
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    // console.log(newSelectedRowKeys, '<<<newSelectedRowKeys');
  };

  const rowSelection = {
    onChange: onSelectChange,
  };
  
  if (isGroupsAndUsersLoading || isLoadingRooms || isLoadingUserPermissions || !userFoldersPermissions) {   
    return (
      <TableLoader padding={'0px'} height={'calc(100dvh - 96px)'} margin={'-10px 0px 0px 0px'}/>
    )
  };

  return (
    <div>
      <Table
        locale={{
          emptyText: (
            <div
              style={{
                height: 'calc(100dvh - 177px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Empty
                description={t('Users.table.empty')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </div>
          ),
        }}
        columns={modifiedColumns}
        pagination={false}
        scroll={{
          y: 'calc(100dvh - 143px)',
          // x: 'max-content'
        }}
        size='small'
        className={classes.table}
        dataSource={allUsers}
        rowSelection={rowSelection}
        expandable={{
          expandedRowRender: (record: any) => {
            if (record.type !== 'invite_link') return null;
            const linkRecord = record as IParentInviteRow;
            return (
              <div className={classes.expandedLinkContent}>
                <table className={classes.registeredUsersTable}>
                  <thead>
                    <tr>
                      <th>{t('Users.table.inviteLink.fullName')}</th>
                      <th>E-mail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkRecord.child_invites.map((child) => {
                      const registeredUser = users.find(u => u.id === child.user_id);
                      const fullName = registeredUser
                        ? `${registeredUser.first_name} ${registeredUser.last_name}`
                        : '-';
                      return (
                        <tr key={child.id}>
                          <td>{fullName}</td>
                          <td>{child.email}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          },
          rowExpandable: (record: any) => record.type === 'invite_link' && record.child_invites?.length > 0,
          columnWidth: 32,
        }}
        onRow={(record, rowIndex) => ({
          onClick: () => onRowClick(record, rowIndex),
        })}
        rowClassName={(record) => {
          return userWithIdProcessed.includes(record.id)
            ? classes.tableRowLoading
            : record.type === 'user' 
              ? classes.tableRow
              : 'null'
        }}
      />

      {isConfirmDeleteOpen && (
        <ConfirmDeleteUser
          isOpen={isConfirmDeleteOpen}
          onCancel={() => dispatch(setIsConfirmDeleteOpen(false))}
          submit={() => dispatch(deleteUserHandle({t}))}
        />
      )}

      {isConfirmBlockOpen && (
        <ConfirmChangeStatus
          isOpen={isConfirmBlockOpen}
          onCancel={() => dispatch(setIsConfirmBlockOpen(false))}
          changeBlockStatusUser={() => dispatch(changeBlockStatus({t}))}
        />
      )}
    </div>
  );
};

export default UsersTable;
